import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * Props for the PageLayout component.
 */
export interface PageLayoutProps extends HTMLAttributes<HTMLElement> {
  /** Additional CSS classes. */
  className?: string;
  /** Page content. */
  children: ReactNode;
}

/**
 * Main page wrapper with max-width constraint and horizontal padding.
 * Use as the outermost layout container on each page.
 *
 * @example
 * ```tsx
 * <PageLayout>
 *   <PageHeader title="Events" actions={<Button>New Event</Button>} />
 *   <PageSection title="Upcoming">
 *     ...
 *   </PageSection>
 * </PageLayout>
 * ```
 */
export const PageLayout = forwardRef<HTMLElement, PageLayoutProps>(function PageLayout(
  { className, children, ...props },
  ref,
) {
  return (
    <main
      ref={ref}
      className={cn('mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8', className)}
      {...props}
    >
      {children}
    </main>
  );
});

/**
 * Props for the PageHeader component.
 */
export interface PageHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Page title displayed as h1. */
  title: string;
  /** Optional slot for action buttons rendered on the right side. */
  actions?: ReactNode;
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Page header with a title and an optional right-aligned actions slot.
 *
 * @param props.title - The page title
 * @param props.actions - Optional action buttons (e.g. create button)
 * @example
 * ```tsx
 * <PageHeader title="Events" actions={<Button>New Event</Button>} />
 * ```
 */
export function PageHeader({ title, actions, className, ...props }: PageHeaderProps) {
  return (
    <div className={cn('mb-6 flex items-center justify-between gap-4', className)} {...props}>
      <h1 className="text-2xl font-bold tracking-tight text-ink">{title}</h1>
      {actions != null && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Props for the PageSection component.
 */
export interface PageSectionProps extends HTMLAttributes<HTMLElement> {
  /** Optional section title displayed as h2. */
  title?: string;
  /** Additional CSS classes. */
  className?: string;
  /** Section content. */
  children: ReactNode;
}

/**
 * Grouped content section within a page, with an optional heading.
 *
 * @param props.title - Optional section heading
 * @example
 * ```tsx
 * <PageSection title="Upcoming Events">
 *   <EventList />
 * </PageSection>
 * ```
 */
export function PageSection({ title, className, children, ...props }: PageSectionProps) {
  return (
    <section className={cn('mb-8', className)} {...props}>
      {title != null && <h2 className="mb-4 text-lg font-semibold text-ink">{title}</h2>}
      {children}
    </section>
  );
}
